import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiUser, FiDollarSign, FiSlash, FiCheckCircle, FiPlus } from 'react-icons/fi';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const AdminUsers = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all'); // 'all', 'registered', 'guest'

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const token = localStorage.getItem('adminToken');
            const res = await axios.get(`${API_URL}/admin/users`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUsers(res.data.users);
        } catch (err) {
            console.error('Failed to fetch users', err);
        } finally {
            setLoading(false);
        }
    };

    const filteredUsers = users.filter(user => {
        if (filter === 'registered') return !user.isGuest && (user.email || user.phoneNumber);
        if (filter === 'guest') return user.isGuest || (!user.email && !user.phoneNumber);
        return true;
    });

    const handleAdjustBalance = async (userId, currentEmail) => {
        const amountStr = prompt(`Enter MINUTES to adjust for ${currentEmail || 'this user'}:\n(Use positive to add minutes, negative to deduct minutes)`);
        if (amountStr === null) return;

        const amount = parseFloat(amountStr);
        if (isNaN(amount)) return alert('Invalid number');

        const reason = prompt('Enter reason for adjustment:');

        try {
            const token = localStorage.getItem('adminToken');
            await axios.post(`${API_URL}/admin/users/${userId}/adjust-balance`,
                { amount, reason },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            alert('Balance adjusted successfully');
            fetchUsers();
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to adjust balance');
        }
    };

    const handleToggleBlock = async (userId, isCurrentlyBlocked) => {
        if (!window.confirm(`Are you sure you want to ${isCurrentlyBlocked ? 'unblock' : 'BLOCK'} this user?`)) return;

        try {
            const token = localStorage.getItem('adminToken');
            await axios.post(`${API_URL}/admin/users/${userId}/toggle-block`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchUsers();
        } catch (err) {
            alert('Failed to update user status');
        }
    };

    if (loading) return <div>Loading User Database...</div>;

    return (
        <div className="animate-fade">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                <h1 style={{ margin: 0 }}>User Management</h1>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                        onClick={() => setFilter('all')}
                        style={{
                            padding: '0.5rem 1rem',
                            background: filter === 'all' ? 'var(--primary)' : 'rgba(255,255,255,0.05)',
                            border: '1px solid var(--border)',
                            borderRadius: '0.5rem',
                            color: filter === 'all' ? 'white' : 'var(--text-muted)',
                            cursor: 'pointer',
                            fontSize: '0.875rem'
                        }}
                    >
                        All ({users.length})
                    </button>
                    <button
                        onClick={() => setFilter('registered')}
                        style={{
                            padding: '0.5rem 1rem',
                            background: filter === 'registered' ? 'var(--primary)' : 'rgba(255,255,255,0.05)',
                            border: '1px solid var(--border)',
                            borderRadius: '0.5rem',
                            color: filter === 'registered' ? 'white' : 'var(--text-muted)',
                            cursor: 'pointer',
                            fontSize: '0.875rem'
                        }}
                    >
                        Registered ({users.filter(u => !u.isGuest && (u.email || u.phoneNumber)).length})
                    </button>
                    <button
                        onClick={() => setFilter('guest')}
                        style={{
                            padding: '0.5rem 1rem',
                            background: filter === 'guest' ? 'var(--primary)' : 'rgba(255,255,255,0.05)',
                            border: '1px solid var(--border)',
                            borderRadius: '0.5rem',
                            color: filter === 'guest' ? 'white' : 'var(--text-muted)',
                            cursor: 'pointer',
                            fontSize: '0.875rem'
                        }}
                    >
                        Guests ({users.filter(u => u.isGuest || (!u.email && !u.phoneNumber)).length})
                    </button>
                </div>
            </div>

            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                    <thead>
                        <tr style={{ background: 'rgba(255,255,255,0.05)' }}>
                            <th style={{ padding: '1.25rem' }}>User / ID</th>
                            <th style={{ padding: '1.25rem' }}>Type</th>
                            <th style={{ padding: '1.25rem' }}>Wallet</th>
                            <th style={{ padding: '1.25rem' }}>Status</th>
                            <th style={{ padding: '1.25rem' }}>Location / Device</th>
                            <th style={{ padding: '1.25rem' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredUsers.map((user) => (
                            <tr key={user._id} style={{ borderBottom: '1px solid var(--border)', opacity: user.isBlocked ? 0.6 : 1 }}>
                                <td style={{ padding: '1.25rem' }}>
                                    <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>
                                        {user.email || user.phoneNumber ? (
                                            <>
                                                {user.email && <div>📧 {user.email}</div>}
                                                {user.phoneNumber && <div>📱 {user.phoneNumber}</div>}
                                            </>
                                        ) : (
                                            <div style={{ color: 'var(--text-muted)' }}>
                                                {user.sessionId ? `Guest (${user.sessionId.slice(0, 8)}...)` : `Guest User`}
                                            </div>
                                        )}
                                    </div>
                                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                                        <div>ID: {user._id.slice(-8)} • Active: {new Date(user.updatedAt).toLocaleDateString()}</div>
                                        {user.firebaseUid && <div>🔐 Firebase: {user.firebaseUid.slice(0, 8)}...</div>}
                                    </div>
                                </td>
                                <td style={{ padding: '1.25rem' }}>
                                    <span style={{
                                        padding: '4px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 700,
                                        background: user.isGuest ? 'rgba(245, 158, 11, 0.1)' : 'rgba(99, 102, 241, 0.1)',
                                        color: user.isGuest ? '#f59e0b' : '#6366f1'
                                    }}>
                                        {user.isGuest ? 'GUEST' : 'MEMBER'}
                                    </span>
                                </td>
                                <td style={{ padding: '1.25rem' }}>
                                    <div style={{ fontWeight: 800, color: 'var(--success)' }}>{user.wallet_balance} min</div>
                                </td>
                                <td style={{ padding: '1.25rem' }}>
                                    {user.isBlocked ? (
                                        <span style={{ color: 'var(--danger)', fontWeight: 700, fontSize: '0.75rem' }}>BLOCKED</span>
                                    ) : (
                                        <span style={{ color: 'var(--success)', fontWeight: 700, fontSize: '0.75rem' }}>ACTIVE</span>
                                    )}
                                </td>
                                <td style={{ padding: '1.25rem', fontSize: '0.75rem' }}>
                                    {user.lastLocation ? (
                                        <div>
                                            <div style={{ fontWeight: 600, marginBottom: '2px' }}>
                                                🌍 {user.lastLocation.city}, {user.lastLocation.country}
                                            </div>
                                            <div style={{ color: 'var(--text-muted)' }}>
                                                📍 IP: {user.lastIP || 'N/A'}
                                            </div>
                                            {user.lastDevice && (
                                                <div style={{ color: 'var(--text-muted)', marginTop: '2px' }}>
                                                    {user.lastDevice.device === 'mobile' ? '📱' : user.lastDevice.device === 'tablet' ? '📱' : '💻'} {user.lastDevice.browser}/{user.lastDevice.os}
                                                </div>
                                            )}
                                            {user.lastLoginAt && (
                                                <div style={{ color: 'var(--text-muted)', marginTop: '2px', fontSize: '0.7rem' }}>
                                                    🕐 {new Date(user.lastLoginAt).toLocaleString()}
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <span style={{ color: 'var(--text-muted)' }}>No data yet</span>
                                    )}
                                </td>
                                <td style={{ padding: '1.25rem' }}>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <button
                                            onClick={() => handleAdjustBalance(user._id, user.email)}
                                            style={{ padding: '6px 10px', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '4px' }}
                                        >
                                            <FiPlus /> Balance
                                        </button>
                                        <button
                                            onClick={() => handleToggleBlock(user._id, user.isBlocked)}
                                            className={user.isBlocked ? 'secondary' : 'danger-btn'}
                                            style={{
                                                padding: '6px 10px', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '4px',
                                                background: user.isBlocked ? 'var(--success)' : 'var(--danger)',
                                                color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer'
                                            }}
                                        >
                                            {user.isBlocked ? <FiCheckCircle /> : <FiSlash />}
                                            {user.isBlocked ? 'Unblock' : 'Block'}
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AdminUsers;
